import React, { useEffect, useState, useContext } from 'react'
import { Box, Drawer, Typography } from '@mui/material'
import Modal from '../../shared/modal'
import Icon from '../../shared/icon'
import { t } from '../../../i18n'
import axios from 'axios'
import { ReplenishmentOrder } from '../types'
import { formatTenantDate } from '../../../utils/formatTenantDateTime'
import { TIME_SOURCE } from '../../../utils/timeSource'
import ReplenishmentHistoryOrder from './HistoryOrder'
import { LocalizationContext } from '../../shared/contexts'

const scope = "replenishment.sidebar.history"

const CancelReplenishmentItemModal = ({
  replenishmentId, itemType, cancelProductReplenishment, cancelPlateReplenishment,
  cancelOilReplenishment, onClose
}) => {

  const cancelItem = () => {
    if(itemType == 'product') {
      cancelProductReplenishment(replenishmentId)
    }
    if(itemType == 'plates') {
      cancelPlateReplenishment(replenishmentId)
    }
    if(itemType == 'oil') {
      cancelOilReplenishment(replenishmentId)
    }
  }

  return (
    <div className="remove-modal">
      <Modal onClose={onClose}>
        <Box>
          <div className="replenishment-order-modal delete">
            <Typography variant="h6" component="h2">
              {t('cancel_item_modal_text', { scope })}
            </Typography>
            <br/>
            <div className="modal-btn">
              <button
                className="btn btn-secondary"
                onClick={onClose}
              >
                {t('keep')}
              </button>
              <button
                className="btn btn-primary"
                onClick={cancelItem}
              >
                {t('cancel_replenishment', { scope })}
              </button>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  )
}

const ReplenishmentOrderRow = ({
  order,
  editable = false,
  history = false,
  enterReplenishmentEditView = () => {},
  closeSidebar = () => {},
  openCartSidebar = (open) => {},
  setSelectedOrderId = (orderId) => {}
}) => {
  const localization = useContext(LocalizationContext)

  const orderItemsCount = () => {
    return order.productReplenishments.length + order.plateReplenishments.length
  }

  const orderStatus = () => {
    switch(order.state) {
      case 'completed':
        return t('state_completed', { scope, deliveryDate: formatTenantDate(order.deliveryDate, localization) })
      case 'draft':
        return t(order.editableOrderId ? 'state_editing' : 'state_open', { scope })
      case 'sent':
        return t('state_sent', { scope })
      case 'closed':
        return t('state_closed', { scope })
        case 'canceled':
          return t('state_canceled', { scope })
    }
  }

  const editOrder = () => {
    if(order.state == 'draft' && order.editableOrderId) {
      openCartSidebar(true)
    } else {
      enterReplenishmentEditView()
      openCartSidebar(true)
    }
  }

  const statusColor = () => {
    if (order.state == 'draft') {
      if(order.editableOrderId) {
        return 'yellow'
      }
    } else if (order.state == 'canceled') {
      return 'red'
    }
    return 'green'
  }

  return (
    <div className="order-info-wrapper" onClick={() => setSelectedOrderId(order.id)}>
      <div className="order-info">
        <span className="order-id">{t('order_name', { scope, orderId: order.id})}</span>
        <div>
          <span title={TIME_SOURCE.app}>Created at: {formatTenantDate(order.createdAt, localization)}</span>
        </div>
        <div>
          <span>{orderItemsCount()} {t(orderItemsCount() > 1 ? 'items' : 'item', { scope })}</span>
        </div>
        <div className="order-status">
          <span>{t('status', { scope })}</span>
          <span className={statusColor()}> {orderStatus()}</span>
        </div>
        {(order.state == 'sent' || (order.state == 'draft' && !!order.editableOrderId)) &&
          <div>
            <span>{t('planned_delivery', { scope })}</span>
            <span className='delivery-date'>{formatTenantDate(order.deliveryDate, localization)}</span>
          </div>
        }
      </div>
      {editable && <Icon name="editIcon" className="edit-icon" onClick={editOrder}/> }
    </div>
  )
}

export const ReplenishmentHistorySidebar = ({
  sidebarOpen, closeSidebar, activeOrders, fetchActiveOrders, setFlashMessage
}) => {
  const [ordersHistory, setOrdersHistory] = useState<ReplenishmentOrder[]>([])
  const [historyPage, setHistoryPage] = useState(1)
  const [totalHistoryPages, setTotalHistoryPages] = useState(0)
  const [selectedOrderId, setSelectedOrderId] = useState<ReplenishmentOrder | null>(null)
  const [showCancelReplenishmentItemModal, setShowCancelReplenishmentItemModal] = useState(false)
  const [itemToCancel, setItemToCancel] = useState(null)

  const { weight_scale: weightScale } = useContext(LocalizationContext)

  useEffect(() => {
    fetchOrdersHistoryPage()
  }, [historyPage])

  useEffect(() => {
    setShowCancelReplenishmentItemModal(!!itemToCancel)
  }, [itemToCancel])

  const fetchOrdersHistoryPage = (clearExisting = false) => {
    axios.get(`/replenishment_orders/history?page=${historyPage}&format=json`).then(({ data }) => {
      const existing = clearExisting ? [] : ordersHistory

      setOrdersHistory([...existing, ...data.replenishment_orders])
      setTotalHistoryPages(data.pagesCount)
    })
  }

  const nextHistoryPage = () => {
    setHistoryPage(historyPage + 1)
  }

  const refreshOrdersHistory = () => {
    setOrdersHistory([])
    setHistoryPage(1)
    fetchOrdersHistoryPage(true)
  }

  const selectedOrder = selectedOrderId
    ? [...activeOrders, ...ordersHistory].find(order => order.id == selectedOrderId)
    : null

  const cancelProductReplenishment = (productReplenishmentId) => {
    axios.put(`/product_replenishments/${productReplenishmentId}/cancel`, {})
    .then(() => {
      setShowCancelReplenishmentItemModal(false)
      setFlashMessage('success', 'Replenishment item canceled')

      fetchActiveOrders()
      refreshOrdersHistory()
    })
    .catch((error) => {
      setFlashMessage('error', error.response.data)
    })
  }

  const cancelPlateReplenishment = (plateReplenishmentId) => {
    axios.put(`/plate_replenishments/${plateReplenishmentId}/cancel`, {})
    .then(() => {
      setShowCancelReplenishmentItemModal(false)
      setFlashMessage('success', 'Replenishment item canceled')

      fetchActiveOrders()
      refreshOrdersHistory()
    })
    .catch((error) => {
      setFlashMessage('error', error.response.data)
    })
  }

  const cancelOilReplenishment = (oilReplenishmentId) => {
    axios.put(`/oil_replenishments/${oilReplenishmentId}/cancel`, {})
    .then(() => {
      setShowCancelReplenishmentItemModal(false)
      setFlashMessage('success', 'Replenishment item canceled')

      fetchActiveOrders()
      refreshOrdersHistory()
    })
    .catch((error) => {
      setFlashMessage('error', error.response.data)
    })
  }

  const onCancelItemModalClose = () => {
    setShowCancelReplenishmentItemModal(false)
    setItemToCancel(null)
  }

  const cancelItem = (item, itemType) => {
    setItemToCancel({...item, type: itemType})
  }

  return (
    <div>
      {showCancelReplenishmentItemModal &&
        <CancelReplenishmentItemModal
          replenishmentId={!!itemToCancel ? itemToCancel.id : null}
          cancelPlateReplenishment={cancelPlateReplenishment}
          cancelOilReplenishment={cancelOilReplenishment}
          cancelProductReplenishment={cancelProductReplenishment}
          itemType={!!itemToCancel ? itemToCancel.type : null}
          onClose={onCancelItemModalClose}
        />
      }
      <Drawer
        className='replenishment-sidebar'
        anchor='right'
        open={sidebarOpen}
        onClose={closeSidebar}
        style={{zIndex: '2 !important'}}
      >
        <Box
          sx={{ width: 400, padding: '20px 15px', borderRadius: '10px 0 0 10px', zIndex: '2 !important' }}
          role="presentation"
        >
          <div className="replenishment-sidebar-content">
          {!selectedOrder &&
            <>
              <div className="sidebar-top">
                <div className='sidebar-header'>
                  <span className='sidebar-heading'>{t('my_orders', { scope })}</span>
                  <Icon name='close' className="close-icon" onClick={closeSidebar}/>
                </div>
              </div>
              <div className="orders-history">
                <div style={{marginTop: '35px'}}>
                  <p className='sidebar-heading'>{t('active_orders', { scope })}</p>
                  {activeOrders.map(order =>
                    <ReplenishmentOrderRow order={order} key={order.id} setSelectedOrderId={setSelectedOrderId}/>
                  )}
                  {activeOrders.length === 0 &&
                    <p className='text-secondary'>{t('no_active_orders', { scope })}</p>
                  }
                </div>
                <div style={{marginTop: '35px'}}>
                  <p className='sidebar-heading'>{t('orders_history', { scope })}</p>
                  {ordersHistory.map(order =>
                    <ReplenishmentOrderRow order={order} key={order.id} setSelectedOrderId={setSelectedOrderId}/>
                  )}
                  {ordersHistory.length === 0 &&
                    <p className='text-secondary'>{t('no_past_orders', { scope })}</p>
                  }
                </div>
              </div>

              {totalHistoryPages > historyPage &&
                <div className="replenishment-buttons">
                  <button className="btn btn-secondary mt-3" onClick={nextHistoryPage}>
                    {t('show_more', { scope })}
                  </button>
                </div>
              }
            </>
          }
          {selectedOrder &&
            <ReplenishmentHistoryOrder
              replenishmentOrder={selectedOrder}
              closeSidebar={() => setSelectedOrderId(null)}
              cancelItem={cancelItem}
              weightScale={weightScale}
            />
          }
          </div>
        </Box>
      </Drawer>
    </div>
  )
}
